import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchApi, eventsApi } from '../lib/api';
import SubSlotPicker from '../components/event/SubSlotPicker';
import InventorNDAPanel from '../components/event/InventorNDAPanel';
import InventorSchedule from '../components/event/InventorSchedule';

export default function InventorEventDashboard({ eventId, onBack, onNavigate }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [mySlots, setMySlots] = useState([]);
  const [connections, setConnections] = useState([]);
  const [expandedCompany, setExpandedCompany] = useState(null);
  // Slot cancellation state: { slotId, companyName, slotStart }
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // Slot booking confirmation: { companyName, slotTime }
  const [bookingConfirmed, setBookingConfirmed] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventRes, availRes, slotsRes] = await Promise.all([
        fetchApi(`/api/events/${eventId}`),
        fetchApi(`/api/events/${eventId}/available-slots`).catch(() => ({ slots: [] })),
        fetchApi(`/api/events/${eventId}/my-slots`).catch(() => ({ slots: [] })),
      ]);

      setEvent(eventRes.event || eventRes);
      setAvailableSlots(availRes.slots || []);
      setMySlots(slotsRes.slots || []);

      // Fetch connections for NDA context
      try {
        const connRes = await fetchApi('/api/connections/my');
        setConnections(connRes.connections || []);
      } catch {
        setConnections([]);
      }
    } catch (err) {
      console.error('InventorEventDashboard load error:', err);
      setError(err.message);
    }
    setLoading(false);
  }, [eventId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Set of ISO time strings for slots the inventor already has booked (for double-booking prevention, D-18)
  const inventorBookedTimes = useMemo(() => {
    const times = new Set();
    mySlots.forEach(s => {
      if (s.status === 'booked_pending_nda' || s.status === 'confirmed') {
        times.add(new Date(s.slot_start).toISOString());
      }
    });
    return times;
  }, [mySlots]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold text-sm font-logo">PV</span>
          </div>
          <p className="text-sm text-gray-400">Loading your event dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center glass p-8 max-w-md">
          <p className="text-vault-error text-sm font-semibold mb-2">Failed to load event dashboard</p>
          <p className="text-xs text-gray-400 mb-4">{error}</p>
          <button onClick={loadData} className="px-5 py-2 rounded-btn text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Event timing
  const now = new Date();
  const startDate = event?.start_date ? new Date(event.start_date) : null;
  const endDate = event?.end_date ? new Date(event.end_date) : null;
  let eventStatus = 'upcoming';
  if (endDate && now > endDate) eventStatus = 'completed';
  else if (startDate && now >= startDate && endDate && now <= endDate) eventStatus = 'live';

  const daysUntil = startDate ? Math.ceil((startDate - now) / (1000 * 60 * 60 * 24)) : null;

  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDate.toLocaleDateString('en-US', { month: 'long' })} ${startDate.getDate()}–${endDate.getDate()}, ${endDate.getFullYear()}`;
    }
    return `${startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
  };

  const statusBadge = {
    upcoming: { bg: 'bg-blue-100 text-blue-700', label: 'Upcoming' },
    live: { bg: 'bg-emerald-100 text-emerald-700', label: 'Live' },
    completed: { bg: 'bg-gray-100 text-gray-500', label: 'Completed' },
  };

  // Group available slots by company
  const matchesByCompany = {};
  availableSlots.forEach(slot => {
    if (!matchesByCompany[slot.company_id]) {
      matchesByCompany[slot.company_id] = {
        company: slot.company || {},
        companyId: slot.company_id,
        slots: [],
        matchScore: slot.match_score || 0,
      };
    }
    matchesByCompany[slot.company_id].slots.push(slot);
    if ((slot.match_score || 0) > matchesByCompany[slot.company_id].matchScore) {
      matchesByCompany[slot.company_id].matchScore = slot.match_score;
    }
  });
  const matches = Object.values(matchesByCompany);

  // Check if inventor already has a booked/confirmed slot with a company
  const bookedCompanyIds = new Set(
    mySlots.filter(s => s.status === 'booked_pending_nda' || s.status === 'confirmed').map(s => s.company_id)
  );

  // Handle slot cancellation (D-20)
  const handleCancelSlot = async () => {
    if (!cancelConfirm) return;
    setCancelling(true);
    try {
      await eventsApi.cancelSlot(eventId, cancelConfirm.slotId);
      setCancelConfirm(null);
      loadData();
    } catch (err) {
      console.error('Slot cancellation error:', err);
    }
    setCancelling(false);
  };

  const formatSlotTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  // Check if slot is within 48 hours of event start (cancellation locked)
  const isCancellationLocked = (slot) => {
    if (!event?.start_date) return false;
    const eventStart = new Date(event.start_date);
    const now = new Date();
    const hoursUntilEvent = (eventStart - now) / (1000 * 60 * 60);
    return hoursUntilEvent < 48;
  };

  const scoreBg = (score) => score >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';

  return (
    <div>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-vault-gold mb-4 transition-colors">
          ← Back
        </button>
      )}

      {/* EVENT HEADER */}
      <div className="glass p-7 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-[28px] text-vault-navy leading-tight">
              {event?.event_name || event?.name || 'Event'}
            </h1>
            {formatDateRange() && <p className="text-sm text-gray-400 mt-1">{formatDateRange()}</p>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${statusBadge[eventStatus].bg}`}>
              {eventStatus === 'live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />}
              {statusBadge[eventStatus].label}
            </span>
            <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">
              ✓ Registered — $99 paid
            </span>
          </div>
        </div>
        {eventStatus === 'live' && (
          <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Event is live now
          </div>
        )}
        {eventStatus === 'upcoming' && daysUntil > 0 && (
          <p className="text-sm text-blue-600 mt-2">Starts in {daysUntil} day{daysUntil !== 1 ? 's' : ''}</p>
        )}
      </div>

      {/* MY MATCHES */}
      <div className="glass p-7 mb-6">
        <h2 className="font-heading text-xl text-vault-navy mb-2">Your Company Matches</h2>
        <p className="text-sm text-gray-400 mb-5">
          These companies match your ideas at 75% or higher. Book a 15-minute pitch slot with each one before they fill up.
        </p>

        {matches.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-vault-border rounded-2xl">
            <div className="text-4xl mb-3">💡</div>
            <h3 className="font-heading text-lg text-vault-navy mb-2">No matches yet</h3>
            <p className="text-sm text-gray-400 max-w-md mx-auto leading-relaxed">
              Our AI scans for matches every morning. If your ideas score 75% or higher with a registered company, you'll see your booking options here.
            </p>
            <p className="text-xs text-gray-300 mt-3">
              Add ideas to your vault to improve AI matching quality.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map(match => {
              const { company, companyId, slots: companySlots, matchScore } = match;
              const alreadyBooked = bookedCompanyIds.has(companyId);
              const existingSlot = mySlots.find(s => s.company_id === companyId && s.status !== 'cancelled');
              const conn = connections.find(c => c.company_id === companyId);
              const slotsLeft = companySlots.length;
              const isExpanded = expandedCompany === companyId;

              return (
                <div key={companyId} className="border-2 border-vault-border rounded-2xl p-5 hover:border-vault-gold/30 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {company.logo_url ? (
                        <img src={company.logo_url} alt="" className="w-11 h-11 rounded-btn object-cover" />
                      ) : (
                        <div className="w-11 h-11 rounded-btn bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-base">
                          {(company.company_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-vault-navy">{company.company_name || 'Company'}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} available
                          {slotsLeft === 1 && <span className="text-vault-error font-semibold ml-1">— last one!</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${scoreBg(matchScore)}`}>
                        {matchScore}% Match
                      </span>
                      {alreadyBooked && existingSlot ? (
                        <span className={`px-3 py-1.5 rounded-btn text-xs font-semibold ${
                          existingSlot.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {existingSlot.status === 'confirmed' ? '✓ Confirmed' : 'NDA Pending'}
                        </span>
                      ) : (
                        <button
                          onClick={() => setExpandedCompany(isExpanded ? null : companyId)}
                          className="px-4 py-2 rounded-btn text-sm font-semibold text-white"
                          style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
                        >
                          {isExpanded ? 'Close' : 'Choose Your Time Slot'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* SubSlotPicker: per-rep time grid with 15-min cells (D-14 through D-18) */}
                  {isExpanded && !alreadyBooked && (
                    <div style={{ marginTop: 12 }}>
                      {/* Group available slots by rep (company hour) if they have per-rep data */}
                      {companySlots.length > 0 ? (
                        <SubSlotPicker
                          eventId={eventId}
                          companyHour={{
                            rep_name: companySlots[0]?.rep_name || null,
                            rep_department: companySlots[0]?.rep_department || null,
                            hour_start: companySlots[0]?.slot_start,
                            hour_end: companySlots[companySlots.length - 1]?.slot_end || companySlots[0]?.slot_end,
                            company_id: companyId,
                          }}
                          subSlots={companySlots}
                          inventorBookedTimes={inventorBookedTimes}
                          currentUserId={user?.id}
                          onBooked={() => {
                            setExpandedCompany(null);
                            setBookingConfirmed({
                              companyName: company.company_name || 'Company',
                              slotTime: companySlots[0]?.slot_start,
                            });
                            loadData();
                          }}
                          onCancel={() => setExpandedCompany(null)}
                        />
                      ) : (
                        <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', padding: 8 }}>
                          No available slots with this company.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Slot booking confirmation card (D-19) */}
                  {alreadyBooked && existingSlot && bookingConfirmed?.companyName === (company.company_name || 'Company') && (
                    <div style={{
                      background: '#ECFDF5',
                      border: '1px solid #10B981',
                      borderRadius: 12,
                      padding: 12,
                      marginTop: 8,
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#10B981', fontFamily: 'Poppins, sans-serif' }}>
                        Slot confirmed &mdash; {bookingConfirmed.companyName}{bookingConfirmed.slotTime ? `, ${formatSlotTime(bookingConfirmed.slotTime)}` : ''}
                      </span>
                    </div>
                  )}

                  {/* Slot cancellation UI (D-20) */}
                  {alreadyBooked && existingSlot && existingSlot.status !== 'confirmed' && (
                    <div style={{ marginTop: 8 }}>
                      {cancelConfirm?.slotId === existingSlot.id ? (
                        <div style={{ padding: '8px 0' }}>
                          <p style={{ fontSize: 11, color: '#64748B', fontFamily: 'Poppins, sans-serif', marginBottom: 8 }}>
                            {isCancellationLocked(existingSlot)
                              ? 'Cancellation is closed \u2014 slots lock 48 hours before the event'
                              : 'Cancel this slot? This cannot be undone if the event starts within 48 hours.'}
                          </p>
                          {!isCancellationLocked(existingSlot) && (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button
                                onClick={handleCancelSlot}
                                disabled={cancelling}
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: 8,
                                  border: 'none',
                                  cursor: cancelling ? 'not-allowed' : 'pointer',
                                  background: '#F97066',
                                  color: '#fff',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  fontFamily: 'Poppins, sans-serif',
                                  opacity: cancelling ? 0.7 : 1,
                                }}
                              >
                                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                              </button>
                              <button
                                onClick={() => setCancelConfirm(null)}
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: 8,
                                  border: '1px solid #E2E8F0',
                                  cursor: 'pointer',
                                  background: 'none',
                                  color: '#94A3B8',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                              >
                                Keep It
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => setCancelConfirm({ slotId: existingSlot.id, companyName: company.company_name || 'Company', slotStart: existingSlot.slot_start })}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 11,
                            color: '#94A3B8',
                            fontFamily: 'Poppins, sans-serif',
                            textDecoration: 'underline',
                            padding: 0,
                          }}
                        >
                          Cancel slot
                        </button>
                      )}
                    </div>
                  )}

                  {/* Inline NDA for booked slots */}
                  {alreadyBooked && existingSlot?.status === 'booked_pending_nda' && conn && (
                    <InventorNDAPanel
                      connectionId={conn.id}
                      companyName={company.company_name || 'Company'}
                      companyNdaUrl={conn.company?.nda_document_url}
                      companyNdaLink={conn.company?.nda_link}
                      slot={existingSlot}
                      onRefresh={loadData}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MY SCHEDULE */}
      <div className="mb-6">
        <InventorSchedule
          eventId={eventId}
          slots={mySlots.filter(s => s.status !== 'available')}
          connections={connections}
          onRefresh={loadData}
        />
      </div>

      {/* PITCH PREP */}
      <div className="glass p-7">
        <h2 className="font-heading text-xl text-vault-navy mb-2">Prepare Your Pitch</h2>
        <p className="text-sm text-gray-400 mb-5">Stand out before your meeting.</p>

        <div className="grid md:grid-cols-3 gap-4">
          <button onClick={() => onNavigate?.('pitch')} className="border-2 border-vault-border rounded-2xl p-5 text-left hover:border-vault-gold/50 transition-colors">
            <div className="text-2xl mb-2">✨</div>
            <h3 className="text-sm font-semibold text-vault-navy mb-1">AI Pitch Coach</h3>
            <p className="text-xs text-gray-400 leading-relaxed">Get AI feedback and generate a custom script for each company.</p>
            <span className="inline-block mt-3 text-xs font-semibold text-vault-gold">Open Pitch Builder →</span>
          </button>
          <button onClick={() => onNavigate?.('bom')} className="border-2 border-vault-border rounded-2xl p-5 text-left hover:border-vault-gold/50 transition-colors">
            <div className="text-2xl mb-2">🧮</div>
            <h3 className="text-sm font-semibold text-vault-navy mb-1">Bill of Materials</h3>
            <p className="text-xs text-gray-400 leading-relaxed">Estimate manufacturing costs and retail viability before your meeting.</p>
            <span className="inline-block mt-3 text-xs font-semibold text-vault-gold">Open BOM Builder →</span>
          </button>
          <button onClick={() => onNavigate?.('trends')} className="border-2 border-vault-border rounded-2xl p-5 text-left hover:border-vault-gold/50 transition-colors">
            <div className="text-2xl mb-2">📈</div>
            <h3 className="text-sm font-semibold text-vault-navy mb-1">Trend Angle</h3>
            <p className="text-xs text-gray-400 leading-relaxed">Find the trend that makes your idea timely and urgent.</p>
            <span className="inline-block mt-3 text-xs font-semibold text-vault-gold">View Trends →</span>
          </button>
        </div>
      </div>
    </div>
  );
}
